import { useState } from 'react'
import Modal from '../Modal/Modal'
import './BanModal.css'

const BanModal = ({ isOpen, onClose, client, onConfirm }) => {
  const [banType, setBanType] = useState('permanent') // 'permanent' or 'temporary'
  const [reason, setReason] = useState('')
  const [expirationDate, setExpirationDate] = useState('')
  const [expirationTime, setExpirationTime] = useState('23:59')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date()
    return today.toISOString().split('T')[0]
  }

  // Get maximum date (1 year from now)
  const getMaxDate = () => {
    const maxDate = new Date()
    maxDate.setFullYear(maxDate.getFullYear() + 1)
    return maxDate.toISOString().split('T')[0]
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const banData = {}
      
      // Add reason if provided
      if (reason.trim()) {
        banData.reason = reason.trim()
      }

      // Add expiration date if temporary ban
      if (banType === 'temporary' && expirationDate) {
        const dateTime = `${expirationDate}T${expirationTime}:00.000Z`
        banData.until = dateTime
      }

      await onConfirm(banData)
      handleClose()
    } catch (error) {
      console.error('Ban error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setBanType('permanent')
    setReason('')
    setExpirationDate('')
    setExpirationTime('23:59')
    setIsSubmitting(false)
    onClose()
  }

  const isFormValid = () => {
    if (banType === 'temporary') {
      return expirationDate !== ''
    }
    return true
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`🚫 Ban Client: ${client?.username || ''}`}
      modalClassName="ban-modal"
    >
      <form onSubmit={handleSubmit} className="ban-form">
        {/* Ban Type Selection */}
        <div className="ban-form-section">
          <label className="ban-form-label">Ban Type</label>
          <div className="ban-type-options">
            <button
              type="button"
              className={`ban-type-option ${banType === 'permanent' ? 'ban-type-option--active' : ''}`}
              onClick={() => setBanType('permanent')}
            >
              <div className="ban-type-icon ban-type-icon--permanent">
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <defs>
                    <linearGradient id="permBanGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#fca5a5" />
                      <stop offset="100%" stopColor="#ef4444" />
                    </linearGradient>
                  </defs>
                  <circle cx="12" cy="12" r="10" fill="url(#permBanGrad)" stroke="#991b1b" strokeWidth="1.5"/>
                  <path d="M7 7l10 10M17 7l-10 10" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="ban-type-content">
                <span className="ban-type-title">Permanent Ban</span>
                <span className="ban-type-desc">Client will be banned indefinitely</span>
              </div>
            </button>

            <button
              type="button"
              className={`ban-type-option ${banType === 'temporary' ? 'ban-type-option--active' : ''}`}
              onClick={() => setBanType('temporary')}
            >
              <div className="ban-type-icon ban-type-icon--temporary">
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <defs>
                    <linearGradient id="tempBanGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#fdba74" />
                      <stop offset="100%" stopColor="#f97316" />
                    </linearGradient>
                  </defs>
                  <circle cx="12" cy="12" r="10" fill="url(#tempBanGrad)" stroke="#9a3412" strokeWidth="1.5"/>
                  <path d="M12 6v6l4 2" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="ban-type-content">
                <span className="ban-type-title">Temporary Suspension</span>
                <span className="ban-type-desc">Client will be unbanned automatically</span>
              </div>
            </button>
          </div>
        </div>

        {/* Reason Input */}
        <div className="ban-form-section">
          <label htmlFor="ban-reason" className="ban-form-label">
            Reason (Optional)
          </label>
          <textarea
            id="ban-reason"
            className="ban-form-textarea"
            placeholder="Enter the reason for this ban (e.g., 'Violated terms of use', 'Inappropriate behavior')..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={4}
          />
          <div className="ban-form-helper">
            {reason.length}/500 characters
          </div>
        </div>

        {/* Expiration Date (only for temporary) */}
        {banType === 'temporary' && (
          <div className="ban-form-section ban-form-section--highlighted">
            <label className="ban-form-label ban-form-label--required">
              Expiration Date & Time
            </label>
            <div className="ban-datetime-group">
              <div className="ban-form-field">
                <label htmlFor="ban-date" className="ban-form-sublabel">Date</label>
                <input
                  type="date"
                  id="ban-date"
                  className="ban-form-input"
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                  min={getMinDate()}
                  max={getMaxDate()}
                  required
                />
              </div>
              <div className="ban-form-field">
                <label htmlFor="ban-time" className="ban-form-sublabel">Time</label>
                <input
                  type="time"
                  id="ban-time"
                  className="ban-form-input"
                  value={expirationTime}
                  onChange={(e) => setExpirationTime(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="ban-form-helper ban-form-helper--info">
              <svg viewBox="0 0 24 24" width="14" height="14">
                <circle cx="12" cy="12" r="10" fill="#60a5fa" stroke="#1e40af" strokeWidth="1.5"/>
                <path d="M12 8v4M12 16h.01" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              The client will be automatically unbanned at the specified date and time
            </div>
          </div>
        )}

        {/* Preview */}
        <div className="ban-form-preview">
          <div className="ban-preview-title">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path d="M12 3c-5 0-9 4-9 9s4 9 9 9 9-4 9-9-4-9-9-9zm0 16c-3.9 0-7-3.1-7-7s3.1-7 7-7 7 3.1 7 7-3.1 7-7 7z" fill="#94a3b8"/>
              <circle cx="12" cy="12" r="5" fill="#94a3b8"/>
              <circle cx="9" cy="10" r="1.5" fill="#1e293b"/>
              <circle cx="15" cy="10" r="1.5" fill="#1e293b"/>
              <path d="M9 14c0 1.7 1.3 3 3 3s3-1.3 3-3" stroke="#1e293b" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
            </svg>
            Preview
          </div>
          <div className="ban-preview-content">
            <div className="ban-preview-item">
              <span className="ban-preview-label">Client:</span>
              <span className="ban-preview-value">{client?.username || 'N/A'}</span>
            </div>
            <div className="ban-preview-item">
              <span className="ban-preview-label">Type:</span>
              <span className={`ban-preview-badge ban-preview-badge--${banType}`}>
                {banType === 'permanent' ? '⛔ Permanent' : '⏰ Temporary'}
              </span>
            </div>
            {reason && (
              <div className="ban-preview-item">
                <span className="ban-preview-label">Reason:</span>
                <span className="ban-preview-value">{reason}</span>
              </div>
            )}
            {banType === 'temporary' && expirationDate && (
              <div className="ban-preview-item">
                <span className="ban-preview-label">Expires:</span>
                <span className="ban-preview-value">
                  {new Date(`${expirationDate}T${expirationTime}`).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="ban-form-actions">
          <button
            type="button"
            className="ban-form-btn ban-form-btn--cancel"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="ban-form-btn ban-form-btn--confirm"
            disabled={!isFormValid() || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg className="ban-spinner" viewBox="0 0 24 24" width="16" height="16">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.25"/>
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round"/>
                </svg>
                Processing...
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
                </svg>
                Confirm Ban
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default BanModal
